/* VastraGharr Luxe — recently viewed products
 * <vg-recently-viewed> reads localStorage 'vg:recent' (handles, newest first; written by vg-product.js),
 * fetches each product card from /products/{handle}?section_id=vg-card-fragment and renders them
 * into a vg-slider. The section stays hidden when there is nothing to show.
 */
(function () {
  'use strict';

  if (customElements.get('vg-recently-viewed')) return;

  var RECENT_KEY = 'vg:recent';
  var FRAGMENT_SECTION = 'vg-card-fragment';
  var cache = {};

  function readHandles() {
    try {
      var list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      if (!Array.isArray(list)) return [];
      var seen = {};
      return list.filter(function (handle) {
        if (typeof handle !== 'string' || !handle || seen[handle]) return false;
        seen[handle] = true;
        return true;
      });
    } catch (e) {
      return [];
    }
  }

  function forget(handles) {
    if (!handles.length) return;
    try {
      var list = readHandles().filter(function (handle) {
        return handles.indexOf(handle) === -1;
      });
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage unavailable */
    }
  }

  /* Resolves { handle, html } (html '' on failure) or { handle, gone: true } for deleted products.
   * Uses the shared VG.fetchCard queue from vg-theme.js when it is available. */
  function fetchCard(root, handle) {
    if (cache[handle]) return cache[handle];
    var request;
    if (window.VG && typeof window.VG.fetchCard === 'function') {
      request = window.VG.fetchCard(handle).then(function (html) {
        return html === null ? { handle: handle, gone: true } : { handle: handle, html: (html || '').trim() };
      });
    } else {
      var url = root + 'products/' + encodeURIComponent(handle) + '?section_id=' + FRAGMENT_SECTION;
      request = fetch(url, { credentials: 'same-origin' }).then(function (response) {
        if (response.status === 404) return { handle: handle, gone: true };
        if (!response.ok) return { handle: handle, html: '' };
        return response.text().then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var fragment = doc.querySelector('.vg-card-fragment');
          return { handle: handle, html: fragment ? fragment.innerHTML.trim() : '' };
        });
      });
    }
    cache[handle] = request.catch(function () {
      delete cache[handle];
      return { handle: handle, html: '' };
    });
    return cache[handle];
  }

  /* Turns card HTML into nodes; VG.prepareCard waits for card styles and quick add assets. */
  function prepare(html) {
    if (window.VG && typeof window.VG.prepareCard === 'function') {
      return window.VG.prepareCard(html, 'recently-viewed').catch(function () {
        return toFragment(html);
      });
    }
    return Promise.resolve(toFragment(html));
  }

  function toFragment(html) {
    var template = document.createElement('template');
    template.innerHTML = html;
    return template.content;
  }

  customElements.define(
    'vg-recently-viewed',
    class VgRecentlyViewed extends HTMLElement {
      connectedCallback() {
        this.body = this.querySelector('[data-vg-recent-body]');
        this.template = this.querySelector('[data-vg-recent-template]');
        this.note = this.querySelector('[data-vg-recent-note]');
        this.limit = Math.min(Math.max(parseInt(this.dataset.limit, 10) || 8, 1), 12);
        this.root = this.dataset.root || '/';
        if (this.root.slice(-1) !== '/') this.root += '/';
        this.render();
      }

      handles() {
        var exclude = this.dataset.exclude || '';
        return readHandles()
          .filter(function (handle) {
            return handle !== exclude;
          })
          .slice(0, this.limit);
      }

      showEmpty() {
        if (this.hasAttribute('data-design-mode') && this.note) {
          if (this.body) this.body.hidden = true;
          this.note.hidden = false;
          this.hidden = false;
          return;
        }
        this.hidden = true;
      }

      render() {
        var handles = this.handles();
        if (!handles.length || !this.body || !this.template) {
          this.showEmpty();
          return;
        }

        this.hidden = false;
        this.setAttribute('aria-busy', 'true');
        var token = (this.renderToken = {});
        var self = this;

        Promise.all(
          handles.map(function (handle) {
            return fetchCard(self.root, handle);
          })
        ).then(function (results) {
          if (token !== self.renderToken || !self.isConnected) return;
          self.removeAttribute('aria-busy');

          forget(
            results
              .filter(function (result) {
                return result.gone;
              })
              .map(function (result) {
                return result.handle;
              })
          );

          var cards = results.filter(function (result) {
            return result.html;
          });
          if (!cards.length) {
            self.showEmpty();
            return;
          }

          return Promise.all(
            cards.map(function (card) {
              return prepare(card.html).then(function (nodes) {
                return { handle: card.handle, nodes: nodes };
              });
            })
          ).then(function (prepared) {
            if (token !== self.renderToken || !self.isConnected) return;
            var content = self.template.content.cloneNode(true);
            var track = content.querySelector('[data-vg-slider-track]');
            prepared.forEach(function (card) {
              var slide = document.createElement('div');
              slide.className = 'vg-slider__slide';
              slide.setAttribute('role', 'listitem');
              slide.dataset.productHandle = card.handle;
              slide.appendChild(card.nodes);
              track.appendChild(slide);
            });

            self.body.hidden = false;
            self.body.textContent = '';
            self.body.appendChild(content);
            if (self.note) self.note.hidden = true;

            if (window.VG && window.VG.wishlist && typeof window.VG.wishlist.sync === 'function') {
              window.VG.wishlist.sync(self.body);
            }
            if (typeof window.initializeScrollAnimationTrigger === 'function') {
              window.initializeScrollAnimationTrigger(self.body);
            }

            self.dispatchEvent(
              new CustomEvent('vg:recent:rendered', { bubbles: true, detail: { count: prepared.length } })
            );
          });
        });
      }
    }
  );
})();
